"use client";

import CurrencyDisplay from "@/app/components/currencyDisplay";
import ProductImage from "@/app/ec/product/productImage";
import { ProductRecommendationProps } from "@/app/ec/product/recommendations/productRecommendation";
import StarRating from "@/components/design-system/star-rating";
import { useAppSelector } from "@/store/hooks";
import { makeGetBestPriceByMpIdSelector } from "@/store/searchSlice";
import fixURI from "@/utils/fixURI";
import Big from "big.js";
import Link from "next/link";
import { useMemo } from "react";
import styles from "./sidebarRecommendations.module.scss";

export default function SidebarRecommendation({
  isLoadingRecommendation = false,
  title = "This is a loading title name that will not be visible to take up space.",
  mpId = -1,
  brandName = "",
  imageLink = "",
  detailLink = "",
  retailPrice = 0,
  bestPrice = 0,
  numberOfRatings = 0,
  rating = 0,
}: SidebarRecommendationProps) {
  const selectBestPriceByMpId = useMemo(makeGetBestPriceByMpIdSelector, []);
  const productFromServer = useAppSelector((state) => selectBestPriceByMpId(state, mpId));
  const fixedDetailLink = useMemo(() => {
    if (!detailLink) {
      return "";
    }
    return fixURI(detailLink);
  }, [detailLink]);

  const adjustedRetailPrice = useMemo(() => {
    try {
      if (!productFromServer) {
        if (new Big(bestPrice).gte(retailPrice)) {
          return 0;
        } else {
          return retailPrice;
        }
      } else {
        if (!productFromServer.retailPrice) {
          return retailPrice;
        } else if (new Big(productFromServer.unitPrice).gte(productFromServer.retailPrice)) {
          return 0;
        } else {
          return productFromServer.retailPrice;
        }
      }
    } catch (error) {
      console.error(error);
      return retailPrice;
    }
  }, [bestPrice, productFromServer, retailPrice]);

  return (
    <div className={`${styles.SidebarRecommendation} ${isLoadingRecommendation ? styles.loadingRecommendation : ""}`}>
      {/* Prefetching each recommendation PDP caused heavy strain on the system */}
      <Link href={fixedDetailLink} className={styles.image} prefetch={false}>
        {imageLink ? (
          <ProductImage src={imageLink} alt={brandName} width={96} height={96} />
        ) : (
          <div className={styles.noImage}></div>
        )}
      </Link>

      <Link href={fixedDetailLink} className={styles.brand} prefetch={false}>
        {brandName}
      </Link>
      <Link href={fixedDetailLink} className={styles.title} title={title} prefetch={false}>
        {title}
      </Link>
      {numberOfRatings > 0 && (
        <Link href={fixedDetailLink} className={styles.rating} prefetch={false}>
          <StarRating rating={rating} size="small" aria-label={`${rating.toFixed(1)} out of 5 stars`} />
          <span className={styles.ratingCount}>({numberOfRatings})</span>
        </Link>
      )}
      <Link href={fixedDetailLink} className={styles.pricing} prefetch={false}>
        <span className={styles.bestPrice}>
          <CurrencyDisplay value={productFromServer?.unitPrice ? productFromServer.unitPrice : bestPrice} />
        </span>
        {adjustedRetailPrice > 0 && (
          <del className={styles.retail}>
            <CurrencyDisplay value={adjustedRetailPrice} />
          </del>
        )}
      </Link>
    </div>
  );
}

export type SidebarRecommendationProps = ProductRecommendationProps;
